/* eslint-disable react-hooks/exhaustive-deps */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setMicLevel,
  setIsTranscriptTimeout,
  resetMicrophoneState,
  selectMicLevel,
  selectIsTranscriptTimeout,
} from '../../../../store/slices/microphone.slice';
import { Button } from '@insight-health-ai/roseate/components';
import { Microphone01 } from '@insight-health-ai/roseate/icons';
import { useWakeLock } from 'react-screen-wake-lock';
import { doStream, endStream } from '../../utils/listen';
import {
  useAuth,
  useFeatureEntitlements,
  usePermissionEntitlements,
} from '@frontegg/react';
import {
  completeEncounter,
  updateEncounter,
  startEncounter,
} from '../../store/actions/encounter';
import { MicrophoneLevel } from './MicrophoneLevel';
import AmbientAnimation from '../CurrentEncounter/AmbientAnimation';
import AmbientAnimationPaused from '../CurrentEncounter/AmbientAnimationPaused';
import { PauseOutlined } from '@ant-design/icons';
import TemplateSelectDropdown from '../Settings/Settings.TemplateDropdown';
import { Modal } from 'antd';
import { Bars } from 'react-loader-spinner';
import { ButtonGroup } from './ButtonGroup';
import { updateCurrentPractitionerSettings } from '../../../../store/actions/practitioner.action';
import { analytics } from 'src/services';

import GenerateSummaryButton from './GenerateSummaryButton';
import {
  DEFAULT_FORMAT,
  DEFAULT_VERBOSITY,
  formatOptions,
  verbosityOptions,
} from '../../utils/encounterConstant';
import { useResolvedVerbosity } from 'src/hooks/encounter/useResolvedEncounterConfig';

const InVisitButton = forwardRef(
  (
    {
      selectedMicrophone,
      setActiveTab,
      storedParams,
      selectedRecord,
      encounterStatus,
      encounterPhase,
      setEncounterStatus,
      setLiveTranscript,
      setRecord,
      setLivePartialTranscript,
      setActiveRightPanelTab,
      isStreaming,
      setIsStreaming,
      isRecording,
      setIsRecording,
      schedulepage,
      restrictTemplates,
      autoResumeVisit,
      noTemplate,
      showSaveDeleteButtons,
      setShowSaveDeleteButtons,
    },
    ref
  ) => {
    const [isStarting, setIsStarting] = useState(false);

    const { isEntitled: hasGlobalNoteFormatEnabled } = useFeatureEntitlements(
      'global-note-format-preference'
    );

    const { isEntitled: hasICD10Access } = useFeatureEntitlements('icd-10');

    const { isEntitled: isInVisitStartAllowed } = usePermissionEntitlements(
      'encounter.in-visit.start'
    );
    const { currentPractitioner, currentPractitionerSettings } = useSelector(
      (state) => state?.practitionerState
    );
    const { encounterDetails, mobileViewStatus, isEncounterDetailsLoading } =
      useSelector((state) => state?.encounterState);
    const micLevel = useSelector(selectMicLevel);
    const isTranscriptTimeout = useSelector(selectIsTranscriptTimeout);

    const { verbosity: resolvedPractitionerVerbosity } = useResolvedVerbosity({
      practitioner_id: encounterDetails?.practitioner_id,
      encounter_verbosity: encounterDetails?.summary_verbosity,
      currentPractitionerSettings,
      currentPractitionerId: currentPractitioner?.practitioner_id,
    });

    const dispatch = useDispatch();
    const { isSupported, released, request, release } = useWakeLock();
    const [currentTemplate, setCurrentTemplate] = useState(null);
    const [templateId, setTemplateId] = useState(null);
    const { user } = useAuth();
    const [templateOverridesId, setTemplateOverridesId] = useState(null);

    const updateCurrentTemplate = useCallback((template) => {
      setCurrentTemplate(template);
      setTemplateId(template?.template_id || null);
      setTemplateOverridesId(template?.template_overrides_id || null);
    }, []);
    const record =
      selectedRecord !== undefined ? selectedRecord : mobileViewStatus;
    const isMobile = window.innerWidth <= 1260;
    useEffect(() => {
      if (!isSupported) {
        console.warn('Screen Wake Lock is not supported');
        return;
      }
      if (isRecording) {
        request().catch((err) => {
          console.error(err);
        });
      } else {
        if (!released) {
          release();
        }
      }
    }, [isRecording, request, release]);

    const [selectedSummaryVerbosity, setSummaryVerbosity] = useState();
    const [selectedSummaryFormat, setSummaryFormat] = useState();

    const summaryVerbosity = useMemo(() => {
      if (selectedSummaryVerbosity) {
        return selectedSummaryVerbosity;
      }

      if (resolvedPractitionerVerbosity) {
        return resolvedPractitionerVerbosity;
      }

      if (currentTemplate?.body?.verbosity) {
        return currentTemplate.body.verbosity;
      }

      return DEFAULT_VERBOSITY;
    }, [
      selectedSummaryVerbosity,
      resolvedPractitionerVerbosity,
      currentTemplate,
    ]);

    const summaryFormat = useMemo(() => {
      if (selectedSummaryFormat) {
        return selectedSummaryFormat;
      }

      if (encounterDetails?.summary_format) {
        return encounterDetails.summary_format;
      }

      const format =
        currentPractitionerSettings?.ambient_in_visit_summary_format;
      if (format) {
        return format;
      }

      if (currentTemplate?.body?.format) {
        return currentTemplate.body.format;
      }

      return DEFAULT_FORMAT;
    }, [selectedSummaryFormat, encounterDetails, currentTemplate]);

    const persistInVisitPreference = () => {
      dispatch(
        updateCurrentPractitionerSettings({
          ambient_in_visit_summary_format: summaryFormat,
          ambient_in_visit_summary_verbosity: summaryVerbosity,
          ambient_in_visit_template_id: currentTemplate?.template_id || null,
          ambient_in_visit_template_overrides_id:
            currentTemplate?.template_overrides_id || null,
        })
      );
    };

    const lastEncounterId = useRef(encounterDetails?.encounter_id);
    useEffect(() => {
      if (lastEncounterId.current !== encounterDetails?.encounter_id) {
        setSummaryVerbosity(undefined);
        setSummaryFormat(undefined);
        setTemplateId(encounterDetails?.in_visit_template_id ?? null);
        setTemplateOverridesId(
          encounterDetails?.in_visit_template_overrides_id ?? null
        );
        lastEncounterId.current = encounterDetails?.encounter_id;
      }
    }, [encounterDetails?.encounter_id]);

    const handleSocketErrors = (error) => {
      analytics.track(
        `User Run Into Socket Error: ${error}, Pausing Encounter`,
        { email: user?.email }
      );
      let modalOptions = {
        centered: true,
        className: 'font-sans',
      };
      if (error === 'MICROPHONE_SETUP_FAILED') {
        Modal.error({
          ...modalOptions,
          title: 'Microphone setup failed',
          content:
            'Unable to access a microphone. Please check your microphone settings and permissions.',
        });
      } else if (error === 'AUDIO_CONTEXT_SUSPENDED') {
        Modal.warning({
          ...modalOptions,
          title: 'Microphone was suspended',
          content:
            'Microphone was suspended. Please click resume to continue transcribing.',
        });
      } else if (error === 'NETWORK_ERROR') {
        Modal.error({
          ...modalOptions,
          title: 'Network error',
          content:
            'You were offline for more than 1 minute. Please click resume to continue transcribing.',
        });
      } else if (error === 'NETWORK_INIT_ERROR') {
        Modal.error({
          ...modalOptions,
          title: 'Network offline',
          content:
            'Could not connect to server. Please check your connection and try again.',
        });
      } else if (error === 'MAX_RECONNECTIONS_EXCEEDED') {
        Modal.error({
          ...modalOptions,
          title: 'Internal server error',
          content:
            'Maximum reconnection attempts reached. Please try again or contact support.',
        });
      } else {
        Modal.error({
          ...modalOptions,
          title: 'Server Error',
          content: 'Could not connect to server. Please try again.',
        });
      }
      handleCleanupRecording();

      const audio = new Audio('src/assets/error.mp3');
      audio.play();
    };

    const handleTranscriptTimeout = (error) => {
      if (error === 'NO_TRANSCRIPTION_RECEIVED') {
        dispatch(setIsTranscriptTimeout(true));
      } else if (error !== 'NO_TRANSCRIPTION_RECEIVED') {
        dispatch(setIsTranscriptTimeout(false));
      }
    };

    const handleCleanupRecording = () => {
      setIsRecording(false);
      setIsStreaming(false);
      endStream();
      dispatch(resetMicrophoneState());
    };

    //Function when pause recording is clicked
    const handlePauseRecording = () => {
      setIsRecording(false);
      setIsStreaming(false);
      endStream();
      dispatch(resetMicrophoneState());
      const noteType =
        encounterPhase === 'in-visit'
          ? 'Visit'
          : encounterPhase === 'pre-chart'
            ? 'Pre-charting'
            : '';
      if (schedulepage) {
        analytics.track(`Clicked Pause ${noteType} In Ambient From VCA`, {
          email: user?.email,
        });
      } else {
        analytics.track(`Clicked Pause ${noteType} In Ambient From Aura`, {
          email: user?.email,
        });
      }
    };

    //Function when resume recording is clicked
    const handleResumeRecording = () => {
      if (
        !['summary_inprogress', 'completed'].includes(
          encounterStatus?.toLowerCase()
        )
      ) {
        persistInVisitPreference();
      }
      let data = {
        summary_verbosity: summaryVerbosity,
        summary_format: summaryFormat,
        template_id: currentTemplate?.template_id,
        template_overrides_id: currentTemplate?.template_overrides_id,
      };
      dispatch(
        updateEncounter(
          record?.encounter_id,
          encounterPhase,
          data,
          storedParams,
          false
        )
      );
      doStream({
        encounterId: record?.encounter_id,
        encounterPhase,
        setMicLevel: (level) => dispatch(setMicLevel(level)),
        setLiveTranscript,
        setLivePartialTranscript,
        selectedMicrophoneId: selectedMicrophone,
        successCallback: () => {
          setIsStreaming(true);
        },
        failureCallback: (error) => {
          handleSocketErrors(error);
        },
        setDictation: () => {},
        isDictate: false,
        transcriptTimeoutCallback: (error) => {
          handleTranscriptTimeout(error);
        },
      });
      setIsRecording(true);
      const noteType =
        encounterPhase === 'in-visit'
          ? 'Visit'
          : encounterPhase === 'pre-chart'
            ? 'Pre-charting'
            : '';
      if (schedulepage) {
        analytics.track(`Clicked Resume ${noteType} In Ambient From VCA`, {
          email: user?.email,
        });
      } else {
        analytics.track(`Clicked Resume ${noteType} In Ambient From Aura`, {
          email: user?.email,
        });
      }
    };

    //Function when stop recording is clicked
    const handleStopRecording = () => {
      setShowSaveDeleteButtons(true);
      handlePauseRecording();
      setIsStreaming(false);
      dispatch(setMicLevel(0));
      if (schedulepage) {
        analytics.track('Clicked Stop Recording In Ambient From VCA', {
          email: user?.email,
        });
      } else {
        analytics.track('Clicked Stop Recording In Ambient From Aura', {
          email: user?.email,
        });
      }
    };

    //Function when complete recording is clicked
    const handleCompleteRecording = async () => {
      const noteType =
        encounterPhase === 'in-visit'
          ? 'Note'
          : encounterPhase === 'after-visit'
            ? 'AVS'
            : 'Pre-chart';

      if (schedulepage) {
        analytics.track(`Clicked Generate ${noteType} In Ambient From VCA`, {
          email: user?.email,
        });
      } else {
        analytics.track(`Clicked Generate ${noteType} In Ambient From Aura`, {
          email: user?.email,
        });
      }

      setIsRecording(false);
      setIsStreaming(false);
      dispatch(setMicLevel(0));
      endStream();

      const completeEncounterCallback = (newRecord) => {
        setRecord(newRecord);
        setShowSaveDeleteButtons(false);
        setEncounterStatus('summary_inprogress');
        setActiveTab?.('note');
        if (hasICD10Access && encounterPhase == 'in-visit') {
          setActiveRightPanelTab && setActiveRightPanelTab('icd10');
        }
      };

      try {
        // First update the encounter with the current template if one is selected
        if (currentTemplate) {
          await dispatch(
            updateEncounter(
              record?.encounter_id,
              encounterPhase,
              {
                template_id: currentTemplate?.template_id,
                template_overrides_id: currentTemplate?.template_overrides_id,
              },
              storedParams,
              false
            )
          );
        }
      } catch (error) {
        console.error('Error updating encounter with template:', error);
        // Continue with completion even if template update fails
      }

      // Complete the encounter (runs regardless of template update success/failure)
      dispatch(
        completeEncounter(
          record?.encounter_id,
          encounterPhase,
          storedParams,
          undefined,
          completeEncounterCallback
        )
      );
    };

    const handleStartEncounter = (encounter_phase) => {
      persistInVisitPreference();
      dispatch(
        startEncounter(
          record?.encounter_id,
          encounter_phase,
          {
            summary_verbosity: summaryVerbosity,
            summary_format: summaryFormat,
            template_id: currentTemplate?.template_id,
            template_overrides_id: currentTemplate?.template_overrides_id,
          },
          storedParams,
          (newRecord) => {
            setIsRecording(true);
            setIsStarting(true);
            setRecord(newRecord);
            doStream({
              encounterId: record?.encounter_id,
              encounterPhase,
              setMicLevel: (level) => dispatch(setMicLevel(level)),
              setLiveTranscript,
              setLivePartialTranscript,
              selectedMicrophoneId: selectedMicrophone,
              successCallback: () => {
                setIsStreaming(true);
                setIsStarting(false);
              },
              failureCallback: (error) => {
                handleSocketErrors(error);
              },
              setDictation: () => {},
              isDictate: false,
              transcriptTimeoutCallback: (error) => {
                handleTranscriptTimeout(error);
              },
            });
          }
        )
      );

      const noteType =
        encounterPhase === 'in-visit'
          ? 'Visit'
          : encounterPhase === 'pre-chart'
            ? 'Pre-charting'
            : '';
      if (schedulepage) {
        analytics.track(`Clicked Start ${noteType} In Ambient From VCA`, {
          email: user?.email,
        });
      } else {
        analytics.track(`Clicked Start ${noteType} In Ambient From Aura`, {
          email: user?.email,
        });
      }
    };

    const ifShowRecordingControls = () => {
      return (
        encounterStatus === 'new' ||
        encounterStatus === 'inprogress' ||
        !encounterDetails?.encounter_id
      );
    };

    const onAuraIconClick = () => {
      if (!isInVisitStartAllowed) {
        return;
      }
      if (encounterStatus === 'new') {
        handleStartEncounter(encounterPhase);
      } else {
        handleResumeRecording();
      }
    };

    const isLoading = !encounterStatus;

    useEffect(() => {
      if (autoResumeVisit && !isLoading && record?.encounter_id) {
        handlePauseResumeToggle();
      }
    }, [autoResumeVisit, record?.encounter_id]);

    const handlePauseResumeToggle = () => {
      if (isRecording) {
        handlePauseRecording();
      } else {
        handleResumeRecording();
      }
    };

    useImperativeHandle(ref, () => {
      return { handlePauseResumeToggle };
    }, [handlePauseResumeToggle]);

    if (noTemplate) {
      return null;
    }

    const getSelectedNoteVerbosity = () => {
      const options = getNoteVerbosityOptions();
      const verbosityExists = options.some(
        (option) => option.value === summaryVerbosity
      );
      return verbosityExists
        ? summaryVerbosity
        : options.length > 0
          ? options[0].value
          : DEFAULT_VERBOSITY;
    };

    const getNoteVerbosityOptions = () => {
      if (
        currentTemplate?.restrict_verbosities &&
        currentTemplate?.restrict_verbosities.length > 0
      ) {
        return verbosityOptions.filter((option) =>
          currentTemplate?.restrict_verbosities.includes(option.value)
        );
      }
      return verbosityOptions;
    };

    return !isLoading ? (
      <>
        <div
          style={{ maxWidth: 'min(400px, 100%)', minWidth: 'min(320px, 100%)' }}
          className="rounded-xl flex flex-col items-center justify-center relative current p-3"
        >
          <div
            className={`${
              isMobile ? 'w-full' : ''
            } flex items-center justify-center`}
          >
            {encounterPhase != 'after-visit' && (
              <div className="">
                {isStreaming && !showSaveDeleteButtons ? (
                  // will trigger once the recording is started/resumed
                  <div
                    className="cursor-pointer"
                    onClick={handlePauseRecording}
                  >
                    <AmbientAnimation />
                    <div className="w-full mb-4">
                      <MicrophoneLevel
                        micLevel={micLevel}
                        isTranscriptTimeout={isTranscriptTimeout}
                      />
                    </div>
                  </div>
                ) : (
                  // will trigger once the recording is paused
                  <div
                    className="cursor-pointer"
                    style={{
                      ...(isEncounterDetailsLoading && {
                        pointerEvents: 'none',
                        cursor: 'not-allowed',
                      }),
                    }}
                    onClick={onAuraIconClick}
                  >
                    <AmbientAnimationPaused />
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="w-full flex justify-center items-center">
            {encounterPhase != 'after-visit' &&
              ifShowRecordingControls() &&
              !showSaveDeleteButtons && (
                <div className="w-full mt-2">
                  <div
                    className={[
                      'w-full',
                      isRecording && 'pointer-events-none opacity-50',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <div className="text-[#667085] mb-1">Note template</div>
                    <TemplateSelectDropdown
                      allowInlineEdit={true}
                      encounterPhase={encounterPhase}
                      currentTemplate={currentTemplate}
                      setCurrentTemplate={updateCurrentTemplate}
                      templateId={templateId}
                      templateOverridesId={templateOverridesId}
                      fromAuraPage={!schedulepage}
                      restrictTemplates={restrictTemplates}
                      encounterDescription={
                        record?.description ??
                        encounterDetails?.description ??
                        null
                      }
                    />
                    {encounterPhase === 'in-visit' && (
                      <>
                        <div className="mt-3">
                          <div className="text-[#667085] mb-1">Note detail</div>
                          <ButtonGroup
                            options={getNoteVerbosityOptions()}
                            value={getSelectedNoteVerbosity()}
                            onChange={(value) => {
                              analytics.track(
                                'Clicked Verbosity Option In In-visit',
                                {
                                  verbosity: value,
                                  email: user?.email,
                                }
                              );
                              setSummaryVerbosity(value);
                            }}
                          />
                        </div>
                        {/* Enable encounter format option if user hasn't migrated to global preferences */}
                        {!hasGlobalNoteFormatEnabled && (
                          <div className="mt-3">
                            <div className="text-[#667085] mb-1">
                              Note format
                            </div>
                            <ButtonGroup
                              options={formatOptions}
                              value={summaryFormat}
                              onChange={(value) => {
                                analytics.track(
                                  'Clicked Format Option In In-visit',
                                  {
                                    format: value,
                                    email: user?.email,
                                  }
                                );
                                setSummaryFormat(value);
                              }}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {isStreaming ? (
                    // will trigger once the recording is started/resumed
                    <>
                      <div className="flex space-x-4 mt-5 justify-center">
                        <Button
                          id={'pause_record'}
                          appearance="danger"
                          variant="outlined"
                          size="lg"
                          block
                          icon={<PauseOutlined />}
                          onClick={handlePauseResumeToggle}
                        >
                          Pause visit
                        </Button>
                      </div>
                      <div className="flex space-x-4 mt-4 justify-center">
                        <GenerateSummaryButton
                          encounter={encounterDetails}
                          encounterPhase={encounterPhase}
                          handleStopRecording={handleStopRecording}
                          handleCompleteRecording={handleCompleteRecording}
                        />
                      </div>
                    </>
                  ) : encounterStatus !== 'new' && !isStarting ? (
                    // will trigger once the recording is paused
                    <>
                      <div className="flex mt-5 justify-center">
                        <div className="bg-[#A9A9A9] hover:bg-[#696969] divide-x divide-white w-full rounded-xl flex justify-center items-center">
                          <Button
                            variant="outlined"
                            size="lg"
                            block
                            onClick={handlePauseResumeToggle}
                            disabled={!isInVisitStartAllowed}
                            icon={<Microphone01 />}
                          >
                            {isRecording ? 'Resuming ...' : 'Resume visit'}
                          </Button>
                        </div>
                      </div>
                      <div className="flex space-x-4 mt-4 justify-center">
                        <GenerateSummaryButton
                          encounter={encounterDetails}
                          handleStopRecording={handleStopRecording}
                          handleCompleteRecording={handleCompleteRecording}
                          encounterPhase={encounterPhase}
                        />
                      </div>
                    </>
                  ) : (
                    <div className="w-full rounded-xl flex justify-center items-center mt-5">
                      <Button
                        id="start_record"
                        variant="outlined"
                        size="lg"
                        block
                        onClick={() => handleStartEncounter(encounterPhase)}
                        loading={isEncounterDetailsLoading}
                        disabled={!isInVisitStartAllowed}
                        icon={<Microphone01 />}
                      >
                        {isRecording ? 'Starting ...' : 'Start visit'}
                      </Button>
                    </div>
                  )}
                </div>
              )}
          </div>
        </div>
      </>
    ) : (
      <>
        <Bars
          height="25"
          width="25"
          color="#00D090"
          ariaLabel="bars-loading"
          wrapperStyle={{}}
          wrapperClass="justify-center"
          visible={true}
        />
      </>
    );
  }
);

InVisitButton.displayName = 'InVisitButton';

export default InVisitButton;
